import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import cn from 'classnames';
import styles from './styles.module.scss';
import Heading from '../../shared/heading';
import Button from '../../shared/button';
import useVideo from '../../../hooks/use-video';
import PhoneIcon from '../../icons/Phone';
import ArrowRightIcon from '../../icons/ArrowRight';
import VolumeCrossedControl from '../MediaHero/icons/VolumeCrossedControl';
import VolumeControl from '../MediaHero/icons/VolumeControl';
import MouseScrollAnimatedIcon from '../../icons/MouseScrollAnimated';
import { useMedia } from 'react-use';

const WistiaPlayerLazy = lazy(() => import('../../shared/wistia-player'));

const ALLOWED_EYEBROW_TAGS = ['h1', 'h2', 'h3', 'p'];

const FullPageHero = ({
  video = null,
  image = null,
  title,
  titleTag = 'h2',
  eyebrow = null,
  eyebrowTag = 'p',
  desc,
  buttonLabel = null,
  buttonLink = null,
  secondaryButtonLink = null,
  secondaryButtonLabel = null,
  videoId = null,
}) => {
  const EyebrowTag = ALLOWED_EYEBROW_TAGS.includes(eyebrowTag) ? eyebrowTag : 'p';
  const { videoWrapperRef, videoRef, isVideoMuted, handleVideoEnd, unmute, mute } = useVideo();
  const mediaContainerRef = useRef(null);
  const wistiaPlayerRef = useRef(null);

  const [isWistiaVideoMuted, setIsWistiaVideoMuted] = useState(true);
  const [isClient, setIsClient] = useState(false);

  const appendImageToCard = useCallback((cardId) => {
    const imageDiv = document.getElementById(`img-${cardId}`);

    if (imageDiv && mediaContainerRef.current) {
      mediaContainerRef.current.appendChild(imageDiv);
    }
  }, []);

  // Handle image appending for this specific card
  useEffect(() => {
    if (image?.id && !video && !videoId) {
      appendImageToCard(image.id);
    }
  }, [image, video, videoId, appendImageToCard]);

  const [headerHeight, setHeaderHeight] = React.useState(0);

  useEffect(() => {
    setHeaderHeight(document.getElementById('mainHeader')?.offsetHeight || 0);
  }, [image]);

  const isMobile = useMedia('(max-width: 767px)', false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleVideoPlay = useCallback(() => {
    if (mediaContainerRef.current) {
      // mediaContainerRef.current.style.display = 'none';
    }
  }, []);

  const isPhone = useMemo(() => buttonLink?.includes('tel:'), [buttonLink]);
  const isSecondaryButtonPhone = useMemo(
    () => secondaryButtonLink?.includes('tel:'),
    [secondaryButtonLink]
  );

  const placeStyleToWistia = useCallback((wistiaElementParam) => {
    const wistiaElement = wistiaElementParam || wistiaPlayerRef.current;
    // This runs after the Wistia player component has been rendered
    // setTimeout(() => {
    // Find the Shadow DOM
    const { shadowRoot } = wistiaElement;
    if (shadowRoot) {
      // Create a style element to inject our CSS
      const styleEl = document.createElement('style');
      styleEl.textContent = `
              /* This CSS is now safely inside the Shadow DOM */
              video {
                object-fit: cover !important;
              }
              .click-for-sound-btn {
                display: none !important;
              }
          `;
      shadowRoot.appendChild(styleEl);
    }
  }, []);

  const handleApiReady = useCallback(
    (event) => {
      const player = event?.target;
      if (!player) return;
      wistiaPlayerRef.current = player;
      placeStyleToWistia(player);
      try {
        setIsWistiaVideoMuted(!!player.muted);
      } catch (e) {
        console.error(e);
      }
    },
    [placeStyleToWistia]
  );

  const toggleWistiaMute = useCallback(() => {
    const player = wistiaPlayerRef.current;
    if (!player) return;
    try {
      const nextMuted = !player.muted;
      player.muted = nextMuted;
      setIsWistiaVideoMuted(nextMuted);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const toggleVideoMute = useCallback(() => {
    if (isVideoMuted) {
      unmute();
    } else {
      mute();
    }
  }, [isVideoMuted, mute, unmute]);

  const videoOnMute = useMemo(() => {
    if (!videoId) {
      return isVideoMuted;
    }
    return isWistiaVideoMuted;
  }, [isWistiaVideoMuted, isVideoMuted, videoId]);

  if (!image && !video && !videoId) {
    return null;
  }
  return (
    <section className={styles.mainHero}>
      <div className={styles.wrapper}>
        <div className={styles.inner}>
          <div className={styles.infoBox}>
            {eyebrow && (!isClient || !isMobile) && (
              <EyebrowTag className={styles.eyebrow}>{eyebrow}</EyebrowTag>
            )}
            {title && (!isClient || !isMobile) && (
              <Heading className={styles.title} tag={titleTag} size="xxl">
                {title}
              </Heading>
            )}
            {desc && <p>{desc}</p>}
            {((buttonLabel && buttonLink) || (secondaryButtonLink && secondaryButtonLabel)) && (
              <div className={styles.btnBox}>
                {buttonLabel && buttonLink && (
                  <Button
                    to={buttonLink}
                    theme="accent-primary"
                    className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
                  >
                    {isPhone && <PhoneIcon />}
                    {buttonLabel}
                    {!isPhone && <ArrowRightIcon />}
                  </Button>
                )}
                {secondaryButtonLink && secondaryButtonLabel && (
                  <Link to={secondaryButtonLink} className={styles.secondaryBtn}>
                    {isSecondaryButtonPhone && <PhoneIcon />}
                    <span>{secondaryButtonLabel}</span>
                    {!isSecondaryButtonPhone && <ArrowRightIcon />}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={styles.mediaBox}
        ref={videoWrapperRef}
        style={{ height: `calc(100vh - ${headerHeight}px)` }}
      >
        {eyebrow && isClient && isMobile && (
          <EyebrowTag className={styles.eyebrow}>{eyebrow}</EyebrowTag>
        )}
        {title && isClient && isMobile && (
          <Heading className={styles.title} tag={titleTag} size="xxl">
            {title}
          </Heading>
        )}

        {(video || (isClient && videoId)) && (
          <div className={styles.videoContainer}>
            {video && !videoId && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                muted={isVideoMuted}
                controls={!isVideoMuted}
                playsInline
                onEnded={handleVideoEnd}
                onPlay={handleVideoPlay}
                ref={videoRef}
              >
                <source src={video.url} type="video/mp4" />
              </video>
            )}
            {isClient && videoId && (
              <Suspense fallback={null}>
                <WistiaPlayerLazy
                  mediaId={videoId}
                  ref={wistiaPlayerRef}
                  onApiReady={handleApiReady}
                  autoPlay
                  preload="none"
                  playlistLoop
                  muted={isWistiaVideoMuted}
                  className={styles.wistiaPlayer}
                  poster={false}
                  swatch={false}
                  controls={false}
                  resumable
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Suspense>
            )}
            <Button
              className={styles.soundToggleButton}
              theme="primary"
              size="sm"
              data-type="video-sound-button"
              aria-label={videoOnMute ? 'Unmute video' : 'Mute video'}
              onClick={!video ? toggleWistiaMute : toggleVideoMute}
            >
              {videoOnMute ? <VolumeCrossedControl /> : <VolumeControl />}
            </Button>
          </div>
        )}

        <div className={styles.imageContainer} ref={mediaContainerRef} />
        {/* {imageUrl && !video && <img src={imageUrl} alt={title} />} */}
      </div>
      <div className={styles.scrollIcon}>
        <MouseScrollAnimatedIcon />
      </div>
    </section>
  );
};

FullPageHero.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
  }),
  image: PropTypes.shape({
    id: PropTypes.number,
    // sizes: PropTypes.shape({
    //   medium: PropTypes.string,
    //   mediumLarge: PropTypes.string,
    //   large: PropTypes.string,
    //   '1536x1536': PropTypes.string,
    // }),
  }),
  title: PropTypes.string.isRequired,
  titleTag: PropTypes.oneOf(['h1', 'h2', 'h3']),
  eyebrow: PropTypes.string,
  eyebrowTag: PropTypes.oneOf(ALLOWED_EYEBROW_TAGS),
  desc: PropTypes.string.isRequired,
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  secondaryButtonLink: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  videoId: PropTypes.string,
};

export default FullPageHero;
